import {
  ScheduleExpander,
  ScheduleHistoryDateSelector,
  SingleScheduleTable,
} from "@/containers/Project/components";
import { MilestoneTableExpanderButton } from "@/containers/Project/components/Milestones/components/Buttons/MilestoneTableExpanderButton";
import { Row, Tooltip } from "antd";
import _ from "lodash";
import { FormattedDate } from "react-intl";
import * as S from "../components/styled";

const colWidth = 200; // minimum col width to keep multiple tables visual aligned horizontally

export const formattedDateValue = (dateValue) => {
  if (!dateValue) {
    return "";
  }
  return <FormattedDate value={new Date(dateValue)} />;
};

/***
 * flattenMilestoneSchedulesForTableData will return the data to be ingested by the table
 * @param milestonesToAdd: list of milestones
 * @param  scheduleId: scheduleId
 * @param includeChildren: includeChildren in schedule table (toggle)
 * @param flattenedMilestones: recursively build list to determine milestone order when flattened
 * @param isMajorMilestone: is top level milestone on schedule, default true then set false when called recursively
 */
export const flattenMilestoneSchedulesForTableData = (
  milestonesToAdd,
  scheduleId,
  includeChildren,
  flattenedMilestones = [],
  topLevelParent = null
) => {
  milestonesToAdd.forEach((milestone) => {
    const flattenedMilestone = {
      id: milestone.id,
      milestoneTemplateId: milestone.milestoneTemplateId,
      milestoneName: (
        <Tooltip title={milestone.displayName ?? milestone.name}>
          <span>{milestone.abbreviation}</span>
        </Tooltip>
      ),
      status: milestone.status,
      defaultDate: formattedDateValue(
        milestone.actualDate ?? milestone.targetDate
      ),
      targetDate: formattedDateValue(milestone.targetDate),
      actualDate: formattedDateValue(milestone.actualDate),
      expander: (
        <MilestoneTableExpanderButton
          milestone={milestone}
          schedule={scheduleId}
          data-test-id="milestone-table-expander-button"
        />
      ),
      topLevelParent: topLevelParent,
    };

    flattenedMilestones.push(flattenedMilestone);

    if (milestone.childMilestones && includeChildren) {
      flattenMilestoneSchedulesForTableData(
        milestone.childMilestones,
        scheduleId,
        true,
        flattenedMilestones,
        topLevelParent ?? milestone.id
      );
    }
  });

  return flattenedMilestones;
};

/***
 * generates antd table from the schedule milestone data
 * @param milestoneSchedules: list of milestones
 * @param expandedSchedules: list indexes of expanded schedules
 * @param toggleSchedule: func to trigger set of expanded schedules
 * @param intl: instance of intl from component
 */
export const generateTableFromFlattenedMilestones = (
  milestoneSchedules,
  expandedSchedules,
  toggleSchedule,
  intl,
  projectId
) => {
  const maxMilestonesCount = milestoneSchedules
    .map((schedule) => schedule.milestones.length)
    .sort((a, b) => a - b)[milestoneSchedules.length - 1];

  const scheduleTableData = [];

  milestoneSchedules.forEach((schedule) => {
    let data = [];

    const col = [
      {
        title: (
          <Tooltip title={schedule.name}>
            <div className="truncate">
              <ScheduleExpander
                schedule={schedule.order}
                expanded={expandedSchedules.includes(schedule.order)}
                onExpand={toggleSchedule}
              />
              {schedule.name}
            </div>
          </Tooltip>
        ),
        dataIndex: "header",
        key: "header",
        fixed: "left",
        className: "schedule-table schedule-selector",
        width: colWidth,
        render: (text) => <div className="truncate">{text}</div>,
      },
    ];

    // add blanks to beginning to reflect schedule branches
    let sourceIndex = 0;
    if (schedule.sourceStart) {
      sourceIndex = milestoneSchedules[0].milestones
        .map((milestone) => milestone.milestoneTemplateId)
        .indexOf(schedule.sourceStart);

      if (sourceIndex > 0) {
        for (let i = 0; i < sourceIndex; i++) {
          col.push({
            title: "",
            dataIndex: "",
            key: "",
            className: `schedule-table status-blank`,
            width: colWidth,
          });
        }
      } else {
        sourceIndex = 0;
      }
    }

    data.push(
      {
        header: intl.formatMessage({ id: "projectMilestoneTable.target" }),
        key: `targetDate`,
      },
      {
        header: schedule.milestones?.length ? (
          <ScheduleHistoryDateSelector
            scheduleId={schedule.id}
            baselineOptions={[]}
            hasPastReviewDate={true}
            loading={true}
          />
        ) : (
          intl.formatMessage({ id: "projectMilestoneTable.review" })
        ),
        key: `reviewDate`,
      },
      {
        header: intl.formatMessage({ id: "projectMilestoneTable.actual" }),
        key: `actualDate`,
      }
    );

    schedule.milestones.forEach((milestone) => {
      data = data.map((datum) =>
        //Return blank dates for skipped milestone
        ({
          ...datum,
          [milestone.id]:
            milestone?.status?.toLowerCase() === "skipped"
              ? null
              : milestone[datum.key],
        })
      );
      col.push({
        title: (
          <Row align="middle" justify="space-around">
            <MilestoneTableExpanderButton
              data-test-id="milestone-expander"
              milestone={milestone}
              schedule={schedule.id}
            />
          </Row>
        ),
        dataIndex: milestone.id,
        key: milestone.id,
        className: `schedule-table status-${milestone.status?.toLowerCase()}`,
        width: colWidth,
      });
    });

    const blankCols =
      maxMilestonesCount - schedule.milestones.length - sourceIndex;

    if (blankCols > 0) {
      for (let i = 0; i < blankCols; i++) {
        col.push({
          title: "",
          dataIndex: "",
          key: `blank-col-${i}`,
          className: `schedule-table status-blank`,
          width: colWidth,
        });
      }
    }

    scheduleTableData.push({ id: schedule.id, data: data, col: col });
  });

  return (
    <S.StyledScrollWrapper>
      <>
        {scheduleTableData.map((schedule, index) => (
          <SingleScheduleTable
            key={schedule.id}
            projectId={projectId}
            schedule={schedule}
            hideRows={!expandedSchedules.includes(index)}
            colWidth={colWidth}
            dateFormatter={formattedDateValue}
          />
        ))}
      </>
    </S.StyledScrollWrapper>
  );
};

/***
 * returns updated data for schedule with historical date data
 * @param data: list of data to populate the table, to be cloned and updated to insert the historical data
 * @param milestoneSnapshots: dictionary of milestone id to {TargetDate: date, ActualDate: date} from date
 * @param asOfDate: date used in history call
 * @param baselineDates: list of date options for dropdown
 * @param updateAsOfDate: function to update as of date in the data
 * @param schedule
 * @param actualDateTooltipText: text for tooltip when value retrieved from actualDate
 * @param targetDateTooltipText: text for tooltip when value retrieved from targetDate
 * @param hasPastReviewDate: bool, if no default review date set to make sure 'selectDate' label places
 */
export const addHistoryToData = (
  milestoneSnapshots,
  asOfDate,
  baselineDates,
  updateAsOfDate,
  schedule,
  actualDateTooltipText,
  targetDateTooltipText,
  hasPastReviewDate
) => {
  const updatedData = _.clone(schedule.data);

  const reviewDateHeader = updatedData.find((da) => da.key === "reviewDate");
  reviewDateHeader.header = (
    <ScheduleHistoryDateSelector
      scheduleId={schedule.id}
      selectedDate={asOfDate}
      baselineOptions={baselineDates}
      updateAsOfDate={updateAsOfDate}
      loading={false}
      hasPastReviewDate={hasPastReviewDate}
    />
  );

  if (milestoneSnapshots) {
    Object.keys(milestoneSnapshots).forEach((milestoneId) => {
      reviewDateHeader[milestoneId] = !schedule?.col
        ?.find((c) => c.dataIndex === milestoneId)
        ?.className?.includes("skipped") ? (
        <Tooltip
          title={
            milestoneSnapshots[milestoneId]?.actualDate
              ? actualDateTooltipText
              : targetDateTooltipText
          }
        >
          <span>
            {formattedDateValue(
              milestoneSnapshots[milestoneId]?.actualDate ||
                milestoneSnapshots[milestoneId]?.targetDate
            )}
          </span>
        </Tooltip>
      ) : (
        <></>
      );
    });
  }
  return updatedData;
};
